"use client";
import * as S from "./styles.ts";
import { CustomerReviewsSectionHeader } from "./Header";
import { CtaOverlay } from "./CtaOverlay";
import { ReviewCard, Skeleton } from "@template/components";
import { useTypedDispatch, useTypedSelector } from "@/redux/helpers/hooks.ts";
import { useEffect } from "react";
import { getStoreReviews } from "@/redux/slices/store/thunks/getStoreReviews.ts";
import TrustPilotReviews from "@template/blocks/CustomerReviewsSection/component/TrustPilotReviews";

interface ICustomerReviewsComponentProps {
  headline?: string;
  cta?: { text: string; href: string };
  internal?: boolean;
  extended?: boolean;
  showFooter?: boolean;
  reviewType?: "custom" | "trustpilot" | "none";
}

const CustomerReviewsSectionComponent = ({
                                           headline,
                                           cta,
                                           extended,
                                           internal,
                                           showFooter,
                                           reviewType = "custom",
                                         }: ICustomerReviewsComponentProps) => {
  const dispatch = useTypedDispatch();
  const reviews = useTypedSelector((state) => state.store.reviews);
  const reviewsLoaded = reviews?.loaded;
  const comments = reviews?.reviews?.comments;

  useEffect(() => {
    if (!reviewsLoaded) {
      dispatch(getStoreReviews());
    }
  }, [reviewsLoaded]);

  return (
    <S.CustomerReviewsSectionWrapper>
      <S.CustomerReviewsSectionContainer>
        <CustomerReviewsSectionHeader
          internal={internal || false}
          headline={headline || ""}
        />
        {reviewType === "custom" ? (
          <S.CustomerReviewsSectionContent $extended={extended}>
            {comments && comments.length > 0
              ? comments.map((review, index) => (
                <ReviewCard
                  key={index}
                  author={review.author}
                  review={review.review}
                />
              ))
              : Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={index}>
                  <ReviewCard
                    key={index}
                    author={`${index}`}
                    review={`${index}`}
                  />
                </Skeleton>
              ))}
          </S.CustomerReviewsSectionContent>
        ) : reviewType === "trustpilot" ? (
          <TrustPilotReviews />
        ) : null}
        <CtaOverlay
          cta={cta}
          showFooter={showFooter}
          hideOverlay={reviewType === "trustpilot"}
        />
      </S.CustomerReviewsSectionContainer>
    </S.CustomerReviewsSectionWrapper>
  );
};

export const YayCustomerReviewsSectionComponent = (
  props: ICustomerReviewsComponentProps,
) => <CustomerReviewsSectionComponent {...props} />;

export default CustomerReviewsSectionComponent;

export const options = [
  {
    description: "",
    displayName: "CustomerReviewsSectionComponent",
    methods: [],
    props: {
      headline: {
        required: false,
        flowType: { name: "string" },
        description: "",
      },
      internal: {
        required: false,
        flowType: { name: "boolean" },
        description: "",
      },
      cta: {
        required: false,
        flowType: {
          name: "signature",
          type: "object",
          raw: "{ text: string; href: string }",
          signature: {
            properties: [
              {
                key: "text",
                value: {
                  name: "string",
                  required: true,
                },
              },
              {
                key: "href",
                value: {
                  name: "string",
                  required: true,
                },
              },
            ],
          },
        },
        description: "",
      },
      reviews: {
        required: false,
        flowType: {
          name: "signature",
          type: "object",
          raw: "{\n  average: string;\n  total: string;\n  comments: {\n    author: string;\n    review: string;\n  }[];\n}",
          signature: {
            properties: [
              {
                key: "average",
                value: {
                  name: "string",
                  required: true,
                },
              },
              {
                key: "total",
                value: {
                  name: "string",
                  required: true,
                },
              },
              {
                key: "comments",
                value: {
                  name: "Array",
                  elements: [
                    {
                      name: "signature",
                      type: "object",
                      raw: "{\n  author: string;\n  review: string;\n}",
                      signature: {
                        properties: [
                          {
                            key: "author",
                            value: {
                              name: "string",
                              required: true,
                            },
                          },
                          {
                            key: "review",
                            value: {
                              name: "string",
                              required: true,
                            },
                          },
                        ],
                      },
                    },
                  ],
                  raw: "{\n  author: string;\n  review: string;\n}[]",
                  required: true,
                },
              },
            ],
          },
        },
        description: "",
      },
      extended: {
        required: false,
        flowType: { name: "boolean" },
        description: "",
      },
    },
  },
];
